import React, { useMemo, useState } from "react";
import { Offcanvas } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "../Common/skeleton.css";
import { useTheme } from "../../ThemeProvider/ThemeContext";

const MobileSidebar = ({ tabs, tabsLoaded }) => {
  const [show, setShow] = useState(false);
  const [activeTab, setActiveTab] = useState("");
  const [activeSubTab, setActiveSubTab] = useState("");
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const visibleTabs = tabs.filter((tab) => !tab.hidden);
  const labelStyle = {
    flex: 1,
    minWidth: 0,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    lineHeight: 1.25,
  };

  const styles = useMemo(() => {
    if (isDark) {
      return {
        // Bottom nav
        navBg: "#0F1424",
        navBorder: "1px solid #1E2642",
        iconColor: "#A6B0CF",
        iconActive: "#4C7DFF",
        iconHoverBg: "#12172A",

        // Offcanvas
        canvasBg: "#0B0F1A",
        canvasBorder: "1px solid #1E2642",
        titleText: "#E8ECF8",
        closeBtnFilter: "invert(1)",

        // Items
        itemText: "#E8ECF8",
        itemMuted: "#A6B0CF",
        itemHoverBg: "#12172A",
        divider: "#1E2642",

        // Pills/active highlight
        activeBg: "rgba(76,125,255,0.14)",
        activeBorder: "1px solid rgba(76,125,255,0.35)",
      };
    }
    return {
      navBg: "#FFFFFF",
      navBorder: "1px solid #E9ECEF",
      iconColor: "#5B6472",
      iconActive: "#0D6EFD",
      iconHoverBg: "#F6F7F9",

      canvasBg: "#FFFFFF",
      canvasBorder: "1px solid #E9ECEF",
      titleText: "#0B1220",
      closeBtnFilter: "none",

      itemText: "#0B1220",
      itemMuted: "#5B6472",
      itemHoverBg: "#F6F7F9",
      divider: "#E9ECEF",

      activeBg: "rgba(13,110,253,0.10)",
      activeBorder: "1px solid rgba(13,110,253,0.25)",
    };
  }, [isDark]);

  const handleNavigation = (tab, subTab = null, event = null) => {
    setShow(false);

    if (subTab) {
      if (event && (event.ctrlKey || event.metaKey)) {
        window.open(subTab.path, "_blank");
      } else {
        setActiveSubTab(subTab.path);
        navigate(subTab.path);
      }
    } else {
      setActiveTab(tab.name);
      if (tab.subTabs.length === 0) {
        if (event && (event.ctrlKey || event.metaKey)) {
          window.open(tab.path, "_blank");
        } else {
          navigate(tab.path);
        }
      }
    }
  };

  if (!tabsLoaded) {
    return (
      <div
        className="position-fixed bottom-0 w-100 d-flex justify-content-around align-items-center border-top"
        style={{ height: "60px", zIndex: 1050, backgroundColor: styles.navBg }}
        aria-busy="true"
        aria-label="Loading navigation"
      >
        {[0, 1, 2, 3, 4].map((key) => (
          <div key={key} className="sk" style={{ width: 24, height: 24, borderRadius: 7 }} />
        ))}
      </div>
    );
  }

  return (
    <>
      {/* Bottom Nav */}
      <div
        className="position-fixed bottom-0 w-100 d-flex justify-content-around align-items-center  border-top"
        style={{ height: "60px", zIndex: 1050, backgroundColor: styles.navBg , color: styles.itemText,}}
      >
        {visibleTabs.slice(0, 4).map((tab) => (
          <button
            key={tab.name}
            className="btn"
            style={{color: styles.itemText, cursor: "pointer"}}
            onClick={(e) =>
              tab.subTabs.length > 0
                ? setShow(true)
                : handleNavigation(tab, null, e)
            }
          >
            <i className={`fa-solid ${tab.icon} fa-lg`} />
          </button>
        ))}

        {/* More */}
        <button className="btn" style={{color: styles.itemText, cursor: "pointer"}} onClick={() => setShow(true)}>
          <i className="fa-solid fa-bars fa-lg" />
        </button>
      </div>

      {/* Slide Drawer */}
      <Offcanvas
        show={show}
        onHide={() => setShow(false)}
        placement="bottom"
        style={{ height: "85%", backgroundColor: styles.canvasBg,borderTop: styles.canvasBorder, }}
      >
        <Offcanvas.Header closeButton  style={{
            borderBottom: `1px solid ${styles.divider}`,
          }}>
          <Offcanvas.Title style={{ color: styles.titleText }}>Menu</Offcanvas.Title>
           <style>{`
            .offcanvas-header .btn-close { filter: ${styles.closeBtnFilter}; }
          `}</style>
        </Offcanvas.Header>

        <Offcanvas.Body style={{ color: styles.itemText,paddingBottom: "60px" }} className="mb-10">
          {visibleTabs.map((tab) => (
            <div key={tab.name} className="mb-3">
              <div
                className="d-flex fw-semibold"
                style={{
                  alignItems: "center",
                  color: styles.itemText,
                  cursor: "pointer",
                  gap: "0.75rem",
                }}
                onClick={(e) => handleNavigation(tab, null, e)}
              >
                <i
                  className={`fa-solid ${tab.icon}`}
                  style={{
                    flex: "0 0 20px",
                    marginTop: "2px",
                  }}
                />
                <div style={labelStyle}>{tab.name}</div>
              </div>

              {tab.subTabs.length > 0 && (
                <div className="ps-4">
                  {tab.subTabs.map((subTab) => (
                    <div
                      key={subTab.name}
                      className="d-flex"
                      style={{
                        alignItems: "center",
                        minHeight: "38px",
                        color: styles.itemMuted,
                        cursor: "pointer",
                        gap: "0.75rem",
                        padding: "8px 0",
                      }}
                      onClick={(e) =>
                        handleNavigation(tab, subTab, e)
                      }
                    >
                      <i
                        className={`fa-solid ${subTab.icon}`}
                        style={{
                          flex: "0 0 20px",
                        }}
                      />
                      <div style={labelStyle}>{subTab.name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          <hr />

          {/* Footer */}
          <div
            className="d-flex align-items-center mb-3"
            onClick={() => {
              setShow(false);
              navigate("/faq")}}
          >
            <i className="fas fa-question-circle me-3" />
            FAQ
          </div>

          <div
            className="d-flex align-items-center mb-3"
            onClick={() => {setShow(false);
              navigate("/profile")}}
          >
            <i className="fas fa-user me-3" />
            Profile
          </div>

          <div
            className="d-flex align-items-center"
            onClick={() => {setShow(false);
              navigate("/logout")}}
          >
            <i className="fas fa-sign-out-alt me-3" />
            Logout
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default MobileSidebar;
